import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Calendar } from "lucide-react";
import { SEOHead, breadcrumbSchema } from "../components/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["All", "Web Design Tips", "SEO & Performance", "Business Tips"];

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    axios.get(`${API}/blog`).then(r => setPosts(r.data)).catch(() => {});
  }, []);

  const filtered = filter === "All" ? posts : posts.filter(p => p.category === filter);

  return (
    <main className="pt-16" data-testid="blog-page">
      <SEOHead
        title="Blog — Web Design Tips & Business Insights"
        description="Web design tips, SEO advice, and business insights from No Fuss Websites. Learn how to get more from your website and grow your Australian business online."
        path="/blog"
        schema={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }])}
      />
      <Breadcrumbs items={[{ name: "Blog", path: "/blog" }]} />
      {/* Hero */}
      <section className="bg-[#0055FF] text-white border-b-4 border-black section-padding">
        <div className="container-main text-center">
          <p className="text-sm font-black uppercase tracking-widest text-[#FFD600] mb-4">Tips & Insights</p>
          <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6 text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
            Free Advice for<br />Aussie Businesses
          </h1>
          <p className="text-xl text-blue-200 font-medium max-w-xl mx-auto">
            Practical tips on websites, SEO, and growing your business online — straight from the No Fuss team.
          </p>
        </div>
      </section>

      {/* Filter */}
      <div className="py-8 bg-[#F4F4F5] border-b-2 border-black">
        <div className="container-main flex flex-wrap gap-3 justify-center">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-5 py-2.5 rounded-full border-2 border-black font-bold text-sm transition-all hover:-translate-y-0.5 ${
                filter === cat ? "bg-[#FFD600] text-black" : "bg-white text-black"
              }`}
              style={{ boxShadow: filter === cat ? "3px 3px 0px 0px rgba(0,0,0,1)" : "" }}
              data-testid={`blog-filter-${cat.toLowerCase().replace(/ /g, "-").replace("&", "and")}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Posts */}
      <section className="section-padding" data-testid="blog-posts-section">
        <div className="container-main">
          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-2xl font-bold">No posts yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((post, i) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group bg-white border-2 border-black rounded-2xl overflow-hidden flex flex-col transition-transform hover:-translate-y-2 duration-200"
                  style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}
                  data-testid={`blog-post-${i}`}
                >
                  {post.image_url && (
                    <div className="h-48 overflow-hidden border-b-2 border-black">
                      <img
                        src={post.image_url}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-black uppercase tracking-widest text-[#0055FF] bg-blue-50 border border-[#0055FF] px-3 py-1 rounded-full">
                        {post.category}
                      </span>
                    </div>
                    <h2 className="text-xl font-black mb-3 group-hover:text-[#0055FF] transition-colors" style={{ fontFamily: "Outfit, sans-serif" }}>
                      {post.title}
                    </h2>
                    <p className="text-sm text-gray-600 leading-relaxed flex-1 mb-6">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-sm border-t-2 border-black pt-4">
                      <span className="font-semibold flex items-center gap-1.5 text-gray-500">
                        <Calendar size={12} />
                        {new Date(post.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                      <span className="font-bold text-[#0055FF] flex items-center gap-1">
                        Read More <ArrowRight size={12} />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#FFD600] border-t-4 border-black">
        <div className="container-main text-center">
          <h2 className="text-4xl font-black mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
            Want Your Own Website?
          </h2>
          <p className="text-xl font-semibold mb-8">Professional websites from $499. Live in 48 hours.</p>
          <Link to="/contact" className="btn-secondary inline-flex items-center gap-2 text-lg" data-testid="blog-cta">
            Get a Free Quote <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </main>
  );
}
